import { Component, Inject, OnInit } from '@angular/core';
import { AssistanceCoverageConfig, AssistanceCoverageName, AssistanceCoveragesComparisonConfig, AssistanceCoverageVariantPricing } from '../../additional-options-section/assistance-coverages-comparison/assistance-coverages-comparison.config';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material';
import { DialogConfigBase } from '../../../../components/dialogs/dialog-config-base';
import { DialogConfigInterface } from '../../../../components/dialogs/dialog-config.interface';
import { BeesafeAssistanceCoverageConfig, BeesafeAssistanceCoveragesComparisonConfig } from './beesafe-assistance-coverages-comparison.config';
import { BeesafeNnwCoverageConfig, BeesafeNnwCoveragesComparisonConfig } from './beesafe-nnw-coverages-comparison.config';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import {StickyCloseDialogComponent} from '../../../../components/dialogs/sticky-close-dialog/sticky-close-dialog.component';
import {DeviceService} from '../../../../services/device.service';
import {OfferModificationConfig} from '../../../../../core/services/impl/offer-modifications/offer-modifications-config.service';

@Component({
  selector: 'app-beesafe-coverage-comparision-dialog',
  templateUrl: './beesafe-coverage-comparison-dialog.component.html',
  styleUrls: ['./beesafe-coverage-comparison-dialog.component.scss']
})
export class BeesafeCoverageComparisonDialogComponent extends StickyCloseDialogComponent implements OnInit {

  coveragesConfig: BeesafeAssistanceCoverageConfig[] | BeesafeNnwCoverageConfig[] ;
  pricingData: AssistanceCoverageVariantPricing[];
  modificationConfig: OfferModificationConfig;
  recommendedVariants: string[] = [];

  mode: 'NNW' | 'ASSISTANCE';

  get assistanceMode(): boolean {
    return this.mode === 'ASSISTANCE'
  }

  private readonly ASSISTANCE_VARIANTS_ORDER = [
    BeesafeAssistanceCoverage.NONE,
    BeesafeAssistanceCoverage.MINI,
    BeesafeAssistanceCoverage.BASIC,
    BeesafeAssistanceCoverage.STANDARD,
    BeesafeAssistanceCoverage.PREMIUM,
  ]

  private readonly NNW_VARIANTS_ORDER = [
    BeesafeNnwCoverage.NONE,
    BeesafeNnwCoverage.MINI,
    BeesafeNnwCoverage.COMFORT
  ]

  static getConfigBuilder(): BeesafeCoverageComparisonDialogConfig {
    return new BeesafeCoverageComparisonDialogConfig();
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
              public deviceService: DeviceService,
              private dialog: MatDialogRef<BeesafeCoverageComparisonDialogComponent>) {
    super(deviceService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.createConfig();
    this.sortPricingData();
    this.initRecommendedVariants();
  }

  close() {
    this.dialog.close();
  }

  selectVariant(name: BeesafeAssistanceCoverage | BeesafeNnwCoverage): void {
    this.dialog.close(name);
  }

  createConfig(): void {
    this.mode = this.data.mode;

    let config;
    if (this.assistanceMode) {
      config = new BeesafeAssistanceCoveragesComparisonConfig();
    } else {
      // NNW
      config = new BeesafeNnwCoveragesComparisonConfig();
    }
    this.coveragesConfig = config.createConfig();
  }

  getCoverageLabel(name: AssistanceCoverageName): string {
    return `ASSISTANCE_COVERAGES_COMPARISON.coveragesLabels.${name}`;
  }

  hasVariant(variantName: string): boolean {
    return !!this.data.pricing.find((pricingItem) => {
      return pricingItem.name === variantName
    });
  }

  isVariantRecommended(variantName: string): boolean {
    return this.recommendedVariants.includes(variantName);
  }

  shouldShowRecommendation(variantName: string, index: number): boolean {
    return index === 0 && this.isVariantRecommended(variantName);
  }

  private sortPricingData(): void {
    const data = [];
    const modeOrder: any = this.assistanceMode ? this.ASSISTANCE_VARIANTS_ORDER : this.NNW_VARIANTS_ORDER;

    data.push({name: false, price: 0}); // item NONE in ASSISTANCE and NNW

    modeOrder.forEach(name => {
      const item = this.data.pricing.find((pricingItem: AssistanceCoverageVariantPricing) => pricingItem.name === name );
      if (isNotNullOrUndefined(item)) {
        data.push(item);
      }
    });
    this.pricingData = data;
  }

  private initRecommendedVariants(): void {
    this.modificationConfig = this.data.config;
    if (this.modificationConfig.hasOwnProperty('isOptionRecommended')) {
      const allVariants = this.assistanceMode ? Object.values(BeesafeAssistanceCoverage) : Object.values(BeesafeNnwCoverage);
      this.recommendedVariants = allVariants.filter(variant => this.modificationConfig.isOptionRecommended(variant));
    }
  }
}

export enum BeesafeAssistanceCoverage {
  NONE = 'ASSISTANCE_NONE',
  MINI = 'ASSISTANCE_MINI',
  BASIC = 'ASSISTANCE_BASIC',
  STANDARD = 'ASSISTANCE_STANDARD',
  PREMIUM = 'ASSISTANCE_PREMIUM',
}

export interface BeesafeAssistanceCoverageVariantPricing {
  name: BeesafeAssistanceCoverage;
  price: number;
}

export enum BeesafeNnwCoverage {
  NONE = 'NONE',
  MINI = 'NNW_20000',
  COMFORT = 'NNW_50000'
}

export class BeesafeCoverageComparisonDialogConfig extends DialogConfigBase implements DialogConfigInterface {
  private config: MatDialogConfig = {
    disableClose: true,
    panelClass: 'app-offers-dialog'
  };

  constructor() {
    super();
  }

  buildConfiguration(data: any): MatDialogConfig {
    return this.buildConfig(data, this.config);
  }
}
